/**
 * Service Status panel — data source health dashboard.
 *
 * Shows all registered sources with their status, last fetch time,
 * circuit breaker state, event count, and freshness indicator.
 * Manual retry button for error-state sources.
 */

import { eventBus } from '@/core/events';
import { sourceManager } from '@/sources/source-manager';
import type { Source, SourceStatus } from '@/sources/base-source';
import { SOURCE_REGISTRY } from '@/config/sources';

export function createServiceStatusPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'service-status-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function getFreshnessColor(source: Source, status: SourceStatus): string {
    if (status.state === 'disabled') return 'var(--text-muted)';
    if (status.state === 'error') return 'var(--severity-critical)';
    if (!status.lastFetch) return 'var(--text-muted)';

    const interval = source.meta.updateInterval;
    const ageMs = Date.now() - new Date(status.lastFetch).getTime();
    const ratio = ageMs / interval;

    if (ratio <= 1) return 'var(--severity-low)';     // Fresh (green)
    if (ratio <= 2) return 'var(--severity-medium)';  // Stale (yellow)
    if (ratio <= 4) return 'var(--severity-high)';    // Very stale (orange)
    return 'var(--severity-critical)';                 // Dead (red)
  }

  function getStateLabel(state: string): string {
    switch (state) {
      case 'active': return 'OK';
      case 'stale': return 'STALE';
      case 'error': return 'ERROR';
      case 'disabled': return 'DISABLED';
      case 'initializing': return 'INIT';
      default: return state.toUpperCase();
    }
  }

  function getStateColor(state: string): string {
    switch (state) {
      case 'active': return 'var(--severity-low)';
      case 'stale': return 'var(--severity-medium)';
      case 'error': return 'var(--severity-critical)';
      case 'disabled': return 'var(--text-muted)';
      case 'initializing': return 'var(--severity-medium)';
      default: return 'var(--text-muted)';
    }
  }

  function formatInterval(ms: number): string {
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60_000)}m`;
  }

  function formatAge(timestamp: string): string {
    const ageMs = Date.now() - new Date(timestamp).getTime();
    if (ageMs < 60_000) return `${Math.round(ageMs / 1000)}s ago`;
    if (ageMs < 3_600_000) return `${Math.round(ageMs / 60_000)}m ago`;
    return `${Math.round(ageMs / 3_600_000)}h ago`;
  }

  function render(): void {
    const sources = sourceManager.getAll();

    contentArea.textContent = '';

    const header = document.createElement('div');
    header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;';
    header.textContent = `Data Sources (${sources.length})`;
    contentArea.appendChild(header);

    if (sources.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; text-align: center; padding: 16px;';
      msg.textContent = 'No sources registered yet';
      contentArea.appendChild(msg);

      // Show configured sources from registry
      const regHeader = document.createElement('div');
      regHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-top: 12px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
      regHeader.textContent = `Configured Sources (${SOURCE_REGISTRY.length})`;
      contentArea.appendChild(regHeader);

      for (const reg of SOURCE_REGISTRY) {
        const row = document.createElement('div');
        row.style.cssText = 'padding: 4px 8px; margin-bottom: 2px; font-size: 11px; color: var(--text-muted);';
        row.textContent = `${reg.name} (${reg.id}) — ${formatInterval(reg.updateIntervalMs)}`;
        contentArea.appendChild(row);
      }

      return;
    }

    for (const source of sources) {
      const status = source.getStatus();
      const freshColor = getFreshnessColor(source, status);
      const stateColor = getStateColor(status.state);

      const card = document.createElement('div');
      card.style.cssText = `
        padding: 8px 10px; margin-bottom: 6px; border-radius: 6px;
        background: var(--bg-secondary); border: 1px solid var(--border-primary);
        border-left: 3px solid ${freshColor};
      `;

      // Top row: name + state badge
      const topRow = document.createElement('div');
      topRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 4px;';

      const name = document.createElement('span');
      name.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary);';
      name.textContent = source.meta.name;
      topRow.appendChild(name);

      const stateBadge = document.createElement('span');
      stateBadge.style.cssText = `
        font-size: 9px; font-weight: 600; text-transform: uppercase;
        padding: 1px 6px; border-radius: 3px; margin-left: auto;
        background: ${stateColor}22; color: ${stateColor};
      `;
      stateBadge.textContent = getStateLabel(status.state);
      topRow.appendChild(stateBadge);

      card.appendChild(topRow);

      // Stats row
      const statsRow = document.createElement('div');
      statsRow.style.cssText = 'display: flex; gap: 12px; font-size: 10px; color: var(--text-muted);';

      const eventCount = document.createElement('span');
      eventCount.textContent = `${status.eventCount} events`;
      statsRow.appendChild(eventCount);

      const interval = document.createElement('span');
      interval.textContent = `every ${formatInterval(source.meta.updateInterval)}`;
      statsRow.appendChild(interval);

      if (status.lastFetch) {
        const lastFetch = document.createElement('span');
        lastFetch.textContent = formatAge(status.lastFetch);
        statsRow.appendChild(lastFetch);
      }

      card.appendChild(statsRow);

      // Error message
      if (status.lastError) {
        const errorRow = document.createElement('div');
        errorRow.style.cssText = 'font-size: 10px; color: var(--severity-critical); margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
        errorRow.textContent = status.lastError;
        errorRow.title = status.lastError;
        card.appendChild(errorRow);
      }

      // Retry button for error state
      if (status.state === 'error') {
        const retryBtn = document.createElement('button');
        retryBtn.type = 'button';
        retryBtn.style.cssText = `
          font-size: 10px; padding: 3px 10px; border-radius: 3px; cursor: pointer;
          background: transparent; color: var(--text-secondary);
          border: 1px solid var(--border-primary); margin-top: 6px;
        `;
        retryBtn.textContent = 'Retry';
        retryBtn.onclick = async () => {
          retryBtn.disabled = true;
          retryBtn.textContent = 'Retrying\u2026';
          try {
            await source.fetch();
            scheduleRender();
          } catch {
            scheduleRender();
          }
        };
        card.appendChild(retryBtn);
      }

      contentArea.appendChild(card);
    }
  }

  // Initial render
  render();

  // Subscribe to source:status events
  cleanupFns.push(
    eventBus.on('source:status', () => scheduleRender()),
  );

  // Also refresh periodically to update "X ago" labels
  const refreshInterval = setInterval(scheduleRender, 30_000);
  cleanupFns.push(() => clearInterval(refreshInterval));

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
